import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Grid2 from '@mui/material/Unstable_Grid2';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { capitalizeEachWord, whatsappLink } from '../../../../../utils/utils';
import { dniFormatter } from '../../../../../utils/formatters';
import { CopyTypography } from '../../../../../Components/CopyTypography';
import { Order, OrderFile } from '../../../../../Models';
import { OrderSection } from '../OrderSection';
import TokenHpButton from './TokenHpButton';
import { usePrescriptionData } from '../../../../../services/api/queries/usePrescriptionData';

dayjs.extend(relativeTime);

const mergePatientAddress = (address: {
    lat: number;
    lng: number;
    input: string;
    decode: string;
    number: string;
    street: string;
    zipcode: string;
    city: string;
    town: string;
    province: string;
}) => `${address.street} ${address.number}, ${address.zipcode}, ${address.city}`;

function PatientInfo({ files }: { files: OrderFile[] }) {
    // Extract unique DNIs from all files
    const uniqueDnis = new Set(
        files
            .map((file) => file.certified_metadata?.patient?.identification)
            .filter((dni) => dni !== undefined && dni !== null),
    );

    // Extract unique affiliate numbers from all files
    const uniqueAffiliateNumbers = new Set(
        files
            .map((file) => file.certified_metadata?.patient?.affiliate_number)
            .filter((affiliateNumber) => affiliateNumber !== undefined && affiliateNumber !== null),
    );

    // Extract unique prescription IDs from all files
    const uniquePrescriptionIds = new Set(
        files
            .map((file) => file.certified_metadata?.prescription_id)
            .filter((id) => id !== undefined && id !== null),
    );

    const dniArray = Array.from(uniqueDnis);
    const affiliateArray = Array.from(uniqueAffiliateNumbers);
    const prescriptionArray = Array.from(uniquePrescriptionIds);

    return (
        <Stack direction="column" spacing={0} alignItems="flex-start">
            <Stack direction="row" justifyContent="start" alignItems="center" spacing={1}>
                <DescriptionIcon color="action" fontSize="small" />
                <Typography variant="body1">{`Receta${files.length > 1 ? 's' : ''}:`}</Typography>
            </Stack>
            {/* Display Prescription IDs (max 5 rows, scroll if overflow) */}
            <Stack sx={{ maxHeight: 5 * 24, overflowY: 'auto' }}>
                {prescriptionArray.map((id) => (
                    <CopyTypography
                        key={`prescription-${id}`}
                        tabIndex={-1}
                        variant="body1"
                        textAlign="left"
                        value={id}
                        message="Receta copiada">
                        {`Id: ${id}`}
                    </CopyTypography>
                ))}
            </Stack>

            {/* Display Affiliate Numbers (max 5 rows, scroll if overflow) */}
            <Stack sx={{ maxHeight: 5 * 24, overflowY: 'auto' }}>
                {affiliateArray.map((affiliateNumber) => (
                    <CopyTypography
                        key={`affiliate-${affiliateNumber}`}
                        tabIndex={-1}
                        variant="body1"
                        textAlign="left"
                        value={affiliateNumber}
                        message="Número de afiliado copiado">
                        {`Afiliado: ${affiliateNumber}`}
                    </CopyTypography>
                ))}
            </Stack>

            {/* Display DNIs (max 5 rows, scroll if overflow) */}
            <Stack sx={{ maxHeight: 5 * 24, overflowY: 'auto' }}>
                {dniArray.map((dni) => (
                    <CopyTypography
                        key={`dni-${dni}`}
                        tabIndex={-1}
                        variant="body1"
                        textAlign="left"
                        value={dni}
                        message="DNI copiado">
                        {`DNI: ${dni}`}
                    </CopyTypography>
                ))}
            </Stack>
        </Stack>
    );
}

function CustomerInfo({ order }: { order: Order }) {
    return (
        <Stack direction="column" spacing={0} justifyContent="flex-start" alignItems="flex-start">
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                <Stack direction="row" justifyContent="start" alignItems="center" spacing={1}>
                    <PersonIcon color="action" fontSize="small" />
                    <Typography id="patient-fullname" variant="body1" noWrap textAlign="left">
                        {capitalizeEachWord(order.patient.fullname)}
                    </Typography>
                </Stack>
                <TokenHpButton order={order} />
            </Stack>

            {order.patient.national_id && (
                <CopyTypography
                    tabIndex={-1}
                    variant="body1"
                    textAlign="left"
                    value={order.patient.national_id}
                    message="Nº de documento copiado"
                    maxWidth="%60">
                    {`DNI: ${dniFormatter(order.patient.national_id)}`}
                </CopyTypography>
            )}
            <Stack direction="row" spacing={4} alignItems="center">
                <CopyTypography
                    tabIndex={-1}
                    variant="body1"
                    textAlign="left"
                    value={order.patient.phone_number}
                    message="Teléfono copiado"
                    maxWidth="%60">
                    {`Tel: ${order.patient.phone_number}`}
                </CopyTypography>
                <IconButton
                    size="small"
                    onClick={() => window.open(whatsappLink(order.patient.phone_number), '_blank')}>
                    <WhatsAppIcon />
                </IconButton>
            </Stack>
            {order.patient.address && (
                <CopyTypography
                    tabIndex={-1}
                    variant="body1"
                    textAlign="left"
                    value={mergePatientAddress(order.patient.address)}
                    message="Dirección copiada"
                    maxWidth="%60">
                    {mergePatientAddress(order.patient.address)}
                </CopyTypography>
            )}
        </Stack>
    );
}

export function PatientCard({ order }: { order: Order }) {
    const { files, anyHasCertifiedMetadata } = usePrescriptionData(order);
    return (
        <OrderSection id="patient-data">
            <Grid2 container>
                {anyHasCertifiedMetadata && (
                    <Grid2
                        xs={4}
                        container
                        direction="column"
                        justifyContent="space-between"
                        alignItems="flex-start">
                        <PatientInfo files={files} />
                    </Grid2>
                )}
                <Grid2 xs direction="column" justifyContent="space-between" alignItems="flex-start">
                    <CustomerInfo order={order} />
                </Grid2>
            </Grid2>
        </OrderSection>
    );
}
